import * as ort from 'onnxruntime-web';

import { LadonOutput, MaskGenerator, PipelineConfig, resourceLoader } from '@webex/ladon-ts';

import { modelManager } from './model-manager';

export class LadonGenerator implements MaskGenerator {
    private lastResult: LadonOutput;
    private lastMask: ort.Tensor;
    private maskModel?: ort.InferenceSession;
    private lastMaskGenerationTime = 0;
    private output?: Float32Array;
    private modelUrl?: string;

    constructor(public readonly config: PipelineConfig) {
        const { height, width } = this.config.mask.outputSize;
        this.lastMask = new ort.Tensor('float32', new Float32Array(height * width), [
            1,
            1,
            height,
            width,
        ]);
        this.lastResult = {
            data: this.lastMask.data,
            facesAndLandmarks: { data: new Float32Array(0), dims: [] },
            gesture: new Float32Array(0),
            motion: 0,
            timestamp: 0,
            warm: false,
        };
    }

    public isLoaded(): boolean {
        return !!this.maskModel;
    }

    /**
     * This method preprocesses an input Uint8ClampedArray to a Float32Array
     * while maintaining memory efficiency by reusing a class-scoped output buffer.
     * If the model rank is 4 (e.g., newer tiny model), it sets an alpha channel value.
     *
     * @param {Uint8ClampedArray} input - The input data to be preprocessed
     * @param {Float32Array} alpha - The alpha channel data
     * @returns {Float32Array} - The processed data
     */
    private preprocess(input: Uint8ClampedArray, alpha: Float32Array): Float32Array {
        const { modelRank, inputSize, outputSize } = this.config.mask;
        const { height, width } = inputSize;
        const numImagePixels = height * width;

        // Initialize the output buffer if it is not already set
        this.output ??= new Float32Array(numImagePixels * modelRank);

        const numInputPixels = input.length / 4; // Divide by 4 for RGBA

        // Directly copy channels (assumes fixed order like BGR or RGB)
        for (let channel = 0; channel < 3; channel++) {
            for (let pixel = 0; pixel < numInputPixels; pixel++) {
                // Map input channels directly to output
                this.output[pixel + channel * numImagePixels] = input[pixel * 4 + (2 - channel)];
            }
        }

        // Scaling factors for width and height
        const widthScale = outputSize.width / width;
        const heightScale = outputSize.height / height;

        // Process alpha channel
        for (let x = 0; x < width; x++) {
            const nearestNeighborX = Math.floor(x * widthScale);
            for (let y = 0; y < height; y++) {
                const nearestNeighborY = Math.floor(y * heightScale);
                const maskIdx = nearestNeighborX + nearestNeighborY * outputSize.width;
                const outputIdx = x + y * width + 3 * numImagePixels;

                // Normalize and scale alpha to [0, 255]
                const normalizedValue = Math.min(Math.max(alpha[maskIdx], 0), 255);
                this.output[outputIdx] = normalizedValue;
            }
        }

        return this.output;
    }

    public getTimeSinceLastGeneration(): number {
        return Date.now() - this.lastMaskGenerationTime;
    }

    public async getLadonOutput(input: Uint8ClampedArray, timestamp: number): Promise<LadonOutput> {
        if (!this.maskModel) {
            throw new Error('[ladon-ts] Mask model not loaded.');
        }

        if (this.getTimeSinceLastGeneration() < this.config.mask.generationIntervalMs) {
            return {
                data: this.lastMask.data,
                facesAndLandmarks: this.lastResult.facesAndLandmarks,
                gesture: this.lastResult.gesture,
                motion: this.lastResult.motion,
                warm: false,
                timestamp,
            };
        }

        this.lastMaskGenerationTime = Date.now();

        const imageTensor = new ort.Tensor('float32', this.preprocess(input, this.lastMask.data), [
            1,
            this.config.mask.modelRank,
            this.config.mask.inputSize.height,
            this.config.mask.inputSize.width,
        ]);

        const results = await this.maskModel.run({ input: imageTensor });
        this.lastMask = results.segmentation;

        this.lastResult = {
            data: this.lastMask.data ?? new Float32Array(0),
            facesAndLandmarks: {
                data: results.faces_and_landmarks?.data ?? new Float32Array(0),
                dims: results.faces_and_landmarks?.dims ?? ([] as readonly number[]),
            },
            gesture: results.gesture?.data ?? new Float32Array(0),
            motion: results.motion?.data[0] ?? 0,
            timestamp,
            warm: true,
        };

        return this.lastResult;
    }

    async load() {
        this.modelUrl = await resourceLoader.resolveUri(this.config, this.config.mask.modelUri);

        // Attempt to retrieve a preloaded InferenceSession from the shared resource manager.
        this.maskModel = modelManager.getModelSession(this.modelUrl);
        // If the model is not preloaded, load it from the provided model URL.
        this.maskModel ??= await modelManager.loadModel(this.config);

        if (!this.maskModel) {
            throw new Error('Failed to load the ONNX model.');
        }
    }

    public async process(): Promise<void> {
        // empty for local generator
    }

    /**
     * Destroys the model session and releases resources.
     * This method should be called when the generator is no longer needed.
     *
     * @returns A promise that resolves when the model is destroyed.
     */
    public async destroy(): Promise<void> {
        if (!this.modelUrl) {
            throw new Error('[ladon-ts] Generator teardown failed. Model URL is not defined.');
        }

        // Clear the model session from the cache.
        await modelManager.destroyModel(this.modelUrl);

        // Release the model session.
        this.modelUrl = undefined;
        this.maskModel = undefined;
        this.output = undefined;
    }
}
